<template>
	<div class="stepWelcome">
		<div class="stepWelcome__brand">
			<img
				:src="logoSrc"
				alt="infra-struktur"
				class="stepWelcome__logo"
			/>
		</div>

		<div class="stepWelcome__head">
			<h2 class="stepWelcome__title">{{ title }}</h2>
			<p class="stepWelcome__subtitle">{{ $t('setupWizard_welcome_subtitle') }}</p>
		</div>

		<ol class="stepWelcome__steps">
			<li
				:key="step.key"
				class="stepWelcome__step"
				v-for="(step, index) in steps"
			>
				<div class="stepWelcome__stepNumber">{{ index + 1 }}</div>
				<div class="stepWelcome__stepText">
					<div class="stepWelcome__stepTitle">{{ step.label }}</div>
					<div class="stepWelcome__stepCaption">{{ step.caption }}</div>
				</div>
			</li>
		</ol>

		<p class="stepWelcome__footnote">{{ $t('setupWizard_welcome_footnote') }}</p>
	</div>
</template>

<script lang="ts">
import type { SetupWizardOverviewStep } from 'src/models/setupWizard';

import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';

import darkLogoSrc from 'src/assets/infrastruktur_logo_dark.svg';
import lightLogoSrc from 'src/assets/infrastruktur_logo_white.svg';
import { useDarkMode } from 'src/composables/useDarkMode';
import { useUserStore } from 'src/stores/user';

export default defineComponent({
	name: 'StepWelcome',
	props: {
		steps: {
			required: true,
			type: Array as PropType<SetupWizardOverviewStep[]>,
		},
	},
	setup() {
		const { t } = useI18n();
		const userStore = useUserStore();
		const { isDark } = useDarkMode();

		const title = computed<string>(() => {
			const user = userStore.authenticatedUserInformation;
			const firstName = user?.firstName?.trim() ?? '';
			const lastName = user?.lastName?.trim() ?? '';
			const fullName = [firstName, lastName].filter(Boolean).join(' ');
			const namePart = fullName !== '' ? `, ${fullName}` : '';
			return t('setupWizard_welcome_title', { name: namePart });
		});

		const logoSrc = computed<string>(() => (isDark.value ? String(lightLogoSrc) : String(darkLogoSrc)));

		return { logoSrc, title };
	},
});
</script>

<style lang="scss" scoped>
/* Vier Übersichts-Karten + Kopf müssen in die feste Body-Höhe des Dialogs (480px) passen,
   ohne Scrollbalken: Rhythmus 0.75rem statt 1rem, Karten 0.75rem statt 0.875rem hoch gepolstert. */
.stepWelcome {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	min-height: 100%;
}

.stepWelcome__brand {
	align-items: center;
	display: flex;
	justify-content: flex-start;
}

.stepWelcome__logo {
	height: 2.25rem;
	width: auto;
}

.stepWelcome__title {
	font-size: var(--text-2xl);
	font-weight: 600;
	letter-spacing: -0.015em;
	margin: 0 0 0.25rem;
}

.stepWelcome__subtitle {
	color: var(--p-text-muted-color);
	font-size: var(--text-sm-plus);
	margin: 0;
}

.stepWelcome__steps {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	list-style: none;
	margin: 0.25rem 0 0;
	padding: 0;
}

.stepWelcome__step {
	align-items: center;
	background: var(--p-surface-0);
	border: 1px solid var(--p-surface-200);
	border-radius: 0.75rem;
	display: flex;
	gap: 1rem;
	padding: 0.75rem 1rem;
}

.stepWelcome__stepNumber {
	align-items: center;
	background: color-mix(in srgb, var(--p-primary-500) 14%, transparent);
	border-radius: 9999px;
	color: var(--p-primary-500);
	display: flex;
	flex-shrink: 0;
	font-size: var(--text-sm);
	font-weight: 700;
	height: 1.875rem;
	justify-content: center;
	width: 1.875rem;
}

.stepWelcome__stepText {
	flex: 1;
	min-width: 0;
}

.stepWelcome__stepTitle {
	color: var(--p-text-color);
	font-size: var(--text-sm-plus);
	font-weight: 600;
	letter-spacing: -0.005em;
}

.stepWelcome__stepCaption {
	color: var(--p-text-muted-color);
	font-size: var(--text-xs-plus);
	line-height: 1.35;
}

.stepWelcome__footnote {
	color: var(--p-text-muted-color);
	font-size: var(--text-xs-plus);
	margin: auto 0 0;
}
</style>

<style lang="scss">
.body--dark .stepWelcome__step {
	background: var(--p-surface-800);
	border-color: var(--p-surface-700);
}

.body--dark .stepWelcome__stepNumber {
	background: color-mix(in srgb, var(--p-primary-400) 22%, transparent);
	color: var(--p-primary-400);
}
</style>
